import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import {
  useAddRequestComment,
  useServiceRequest,
  useUpdateRequestStatus,
} from '@/hooks/use-requests'
import { getApiErrorMessage } from '@/lib/api'
import { fetchRequestAttachmentBlobUrl } from '@/lib/requests'
import { formatDate } from '@/lib/utils'
import type { ServiceRequestStatus } from '@riverpark/types'

/** Fetches the photo once (auth cookie required, so no plain <img src>) and renders it as a
 * thumbnail; clicking opens the same blob at full size in a new tab. */
function AttachmentThumbnail({
  requestId,
  attachmentId,
}: {
  requestId: string
  attachmentId: string
}) {
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    fetchRequestAttachmentBlobUrl(requestId, attachmentId).then((u) => {
      if (cancelled) {
        URL.revokeObjectURL(u)
        return
      }
      objectUrl = u
      setUrl(u)
    })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [requestId, attachmentId])

  if (!url) {
    return <div className="h-20 w-20 animate-pulse rounded border border-border-2 bg-cream-2" />
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt="Attached photo"
        className="h-20 w-20 rounded border border-border-2 object-cover"
      />
    </a>
  )
}

const SELECT_CLS =
  'flex h-9 rounded border border-input bg-card px-3 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const STATUS_BADGE: Record<
  ServiceRequestStatus,
  { variant: 'pending' | 'committee' | 'paid' | 'draft'; label: string }
> = {
  open: { variant: 'pending', label: 'Open' },
  in_progress: { variant: 'committee', label: 'In progress' },
  resolved: { variant: 'paid', label: 'Resolved' },
  closed: { variant: 'draft', label: 'Closed' },
}

const TYPE_LABEL: Record<string, string> = {
  maintenance: 'Maintenance',
  billing_query: 'Billing query',
  complaint: 'Complaint',
  general: 'General',
}

export function RequestDetailModal({
  requestId,
  canChangeStatus,
  onClose,
}: {
  requestId: string
  canChangeStatus: boolean
  onClose: () => void
}) {
  const { data, isLoading } = useServiceRequest(requestId)
  const addComment = useAddRequestComment()
  const updateStatus = useUpdateRequestStatus()

  const [reply, setReply] = useState('')
  const [error, setError] = useState<string | null>(null)

  const request = data?.data

  async function handleReply(e: React.FormEvent) {
    e.preventDefault()
    if (!reply.trim()) return
    setError(null)
    try {
      await addComment.mutateAsync({ requestId, body: reply.trim() })
      setReply('')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to post reply'))
    }
  }

  async function handleStatusChange(status: ServiceRequestStatus) {
    try {
      await updateStatus.mutateAsync({ requestId, status })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to update status'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="request-detail-title" panelClassName="max-w-xl">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            {request && (
              <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
                {TYPE_LABEL[request.type]}
              </p>
            )}
            <h2 id="request-detail-title" className="font-serif text-xl text-ink">
              {request?.subject ?? 'Service request'}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>

      <ModalBody className="space-y-4">
        {isLoading || !request ? (
          <p className="font-sans text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-3">
              <p className="font-sans text-xs text-muted-foreground">
                {request.memberName ? (
                  request.memberName
                ) : (
                  <>
                    <Badge variant="pending">Public</Badge>
                    {request.reporterName && <> · {request.reporterName}</>}
                    {request.reporterContact && <> ({request.reporterContact})</>}
                  </>
                )}
                {request.plotNumber && <> · Plot {request.plotNumber}</>}
                {' · '}
                {formatDate(new Date(request.createdAt * 1000))}
              </p>
              {canChangeStatus ? (
                <select
                  className={SELECT_CLS}
                  value={request.status}
                  onChange={(e) => void handleStatusChange(e.target.value as ServiceRequestStatus)}
                  disabled={updateStatus.isPending}
                >
                  <option value="open">Open</option>
                  <option value="in_progress">In progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              ) : (
                <Badge variant={STATUS_BADGE[request.status].variant}>
                  {STATUS_BADGE[request.status].label}
                </Badge>
              )}
            </div>

            <p className="rounded border border-border-2 bg-cream-2 px-3 py-2.5 font-sans text-sm text-ink">
              {request.body}
            </p>

            {request.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {request.attachments.map((a) => (
                  <AttachmentThumbnail key={a.id} requestId={requestId} attachmentId={a.id} />
                ))}
              </div>
            )}

            <div className="space-y-2.5">
              <p className="font-sans text-label uppercase tracking-[0.12em] text-muted-foreground">
                Replies
              </p>
              {request.comments.length === 0 ? (
                <p className="font-sans text-sm text-muted-foreground">No replies yet.</p>
              ) : (
                request.comments.map((c) => (
                  <div key={c.id} className="rounded border border-border-2 bg-card px-3 py-2">
                    <p className="font-sans text-sm text-ink">{c.body}</p>
                    <p className="mt-1 font-sans text-xs text-muted-foreground">
                      {c.authorName} · {formatDate(new Date(c.createdAt * 1000))}
                    </p>
                  </div>
                ))
              )}
            </div>

            <form onSubmit={handleReply} className="space-y-2">
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                placeholder="Write a reply…"
                className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
              />
              {error && (
                <p className="font-sans text-xs text-destructive" role="alert">
                  {error}
                </p>
              )}
              <div className="flex justify-end">
                <Button type="submit" size="sm" loading={addComment.isPending}>
                  Reply
                </Button>
              </div>
            </form>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  )
}
